import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/buttons/buttons";
import Input from "../../../components/inputs/inputs";
// docsImages
import { XIcon } from "@heroicons/react/outline";
import { Beat } from "../../../plugins/spinners.plugin";
import EditKYCVerification from "./EditKYCVerification";
import {
  storeHumanVerificationAction,
  showMyHumanVerification,
} from "../../../redux/humanVerification/humanVerificationActions";

export default function KYCVerification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hiddenDocsInput = useRef(null);
  const hiddenProofInput = useRef(null);

  const [data, setData] = useState({});
  const [docsImages, setDocsImages] = useState([]);
  const [livePhotos, setLivePhotos] = useState([]);
  const [photosUrl, setPhotosUrl] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const { isLoading, userVerification } = useSelector(
    (state) => state.humanVerification
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(showMyHumanVerification());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setData({ ...data, [name]: value });
  };

  function onImageChange(e) {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024; // 2MB

    // Validate file types (only documents)
    const validDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const invalidTypeFiles = files.filter(file => !validDocTypes.includes(file.type));

    if (invalidTypeFiles.length > 0) {
      toast.error("Only document files (PDF, DOC, DOCX, TXT) are allowed for identity documents", {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
        autoClose: 4000,
      });
      e.target.value = ""; // Reset input
      return;
    }

    // Validate each file size
    const invalidFiles = files.filter(file => file.size > maxSize);

    if (invalidFiles.length > 0) {
      toast.error("Each document file must be less than 2MB", {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
        autoClose: 4000,
      });
      e.target.value = ""; // Reset input
      return;
    }

    setDocsImages([...files]);
  }

  const handleDocsClick = (event) => {
    event.preventDefault();
    hiddenDocsInput.current.click();
  };

  const handleProofClick = (event) => {
    event.preventDefault();
    hiddenProofInput.current.click();
  };

  const removeFile = (index) => {
    let newArr = docsImages;
    newArr.splice(index, 1);
    setDocsImages([...newArr]);
  };

  const removeProofPhoto = (index) => {
    let newArr = livePhotos;
    newArr.splice(index, 1);
    setLivePhotos([...newArr]);

    // Also remove from preview URLs
    let newUrls = photosUrl;
    newUrls.splice(index, 1);
    setPhotosUrl([...newUrls]);
  };

  const uploadProofOfLife = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024; // 2MB

    // Validate file types (only images)
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidTypeFiles = files.filter(file => !validImageTypes.includes(file.type));

    if (invalidTypeFiles.length > 0) {
      toast.error("Only image files (JPEG, PNG, GIF, WebP) are allowed for proof of life", {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
        autoClose: 4000,
      });
      e.target.value = ""; // Reset input
      return;
    }

    // Validate each file size
    const invalidFiles = files.filter(file => file.size > maxSize);

    if (invalidFiles.length > 0) {
      toast.error("Proof of life image must be less than 2MB", {
        position: toast.POSITION.TOP_RIGHT,
        theme: "colored",
        autoClose: 4000,
      });
      e.target.value = ""; // Reset input
      return;
    }

    setLivePhotos([...files]);
  };

  useEffect(() => {
    if (livePhotos.length < 1) return;
    if (livePhotos.length > 0) {
      const newImageUrls = [];
      livePhotos?.forEach((image) => {
        newImageUrls.push(URL.createObjectURL(image));
      });
      setPhotosUrl(newImageUrls);
    }
    // eslint-disable-next-line
  }, [livePhotos]);

  const handleSubmit = async () => {
    setSubmitted(true);

    if (
      data?.document_type &&
      data?.document_number &&
      docsImages?.length > 0 &&
      livePhotos?.length > 0
    ) {
      // Validate array limits
      if (docsImages.length > 3) {
        toast.error("You can only upload up to 3 document images", {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
          autoClose: 4000,
        });
        return;
      }

      if (livePhotos.length > 3) {
        toast.error("You can only upload up to 3 proof of life images", {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
          autoClose: 4000,
        });
        return;
      }

      try {
        const payload = {
          ...data,
          docs: Array.from(docsImages),
          photos: Array.from(livePhotos),
        };

        dispatch(storeHumanVerificationAction(payload));
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  function isObjEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  return (
    <div>
      <div className="sm:w-[820px] w-full mt-10 sm:ml-10 sm:mt-0 px-6 pb-10 bg-white dark:bg-[#121212] sm:h-[1000px] rounded-sm shadow-2xl">
        <div className="border-b-2 py-4 sm:py-6 mb-6 flex sm:block justify-between items-center">
          <p className="text-base font-medium text-gray-800 dark:text-white text-center">
            Personal KYC
          </p>
        </div>
        {userVerification?.status === "approved" ||
          userVerification?.status === "pending" ||
          isObjEmpty(userVerification) ? (
          <form className="sm:px-10">
            <div className="sm:grid sm:grid-cols-1 gap-1">
              {/* grid column one begins */}
              <div className="relative">
                <div className="absolute right-0 text-sm">
                  {userVerification?.status === "approved" && (
                    <span className="px-2 py-1 rounded-md text-[#00D220] bg-[#D1FED8]">
                      Approved
                    </span>
                  )}
                  {userVerification?.status === "pending" && (
                    <span className="px-2 py-1 rounded-md text-[#FFA927] bg-[#FFE2B6]">
                      Pending
                    </span>
                  )}
                  {userVerification?.status === "rejected" && (
                    <span className="px-2 py-1 rounded-md text-primary bg-[#ce260026]">
                      Rejected
                    </span>
                  )}
                </div>
                <Input.Select
                  title={"Select Identity Card"}
                  name="document_type"
                  id="document_type"
                  className="w-full border border-[#E4E4F3] focus-within:border-primary focus:border-primary focus:ring-0 sm:text-sm"
                  htmlFor="document_type"
                  disabled={
                    userVerification?.status === "approved" ||
                    userVerification?.status === "pending"
                  }
                  value={userVerification?.document_type}
                  defaultValue={userVerification?.document_type}
                  onChange={handleChange}
                >
                  <option>Select card type</option>
                  <option>Drivers License</option>
                  <option>International Passport</option>
                  <option>NIN</option>
                </Input.Select>
                {submitted && !data?.document_type && (
                  <div className="text-primary text-xs ">
                    Document Type is required
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="document_number"
                  className="block text-sm font-medium"
                >
                  Enter Identity Card number
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="document_number"
                    id="document_number"
                    className="mt-2 block pl-3 pr-10 w-full text-base bg-[#FFFFFF] dark:bg-transparent focus:ring-2 focus:ring-red-600 focus:border-red-600 focus:outline-none sm:text-sm h-[40px] px-4 py-2 mb-4 border border-gray-300 "
                    placeholder="Enter Identity Card number"
                    defaultValue={userVerification?.document_number}
                    disabled={
                      userVerification?.status === "approved" ||
                      userVerification?.status === "pending"
                    }
                    onChange={(e) => handleChange(e)}
                  />
                </div>
                {submitted && !data?.document_number && (
                  <div className="text-primary text-xs ">
                    ID Card number is required
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="identity_card"
                  className="block text-sm font-medium"
                >
                  Upload Identity Card (Accepts .pdf, .doc, .docx, .txt formats)
                </label>
                <div className="mt-2">
                  <button
                    className="w-full border border-[#E4E4F3] bg-[#FAFAFA] dark:bg-transparent p-3 text-center text-[#9CA3AF]"
                    onClick={handleDocsClick}
                    disabled={
                      userVerification?.status === "approved" ||
                      userVerification?.status === "pending"
                    }
                  >
                    + Upload Document
                  </button>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={onImageChange}
                    ref={hiddenDocsInput}
                  />
                  {submitted && docsImages?.length < 1 && (
                    <div className="text-primary text-xs ">
                      Identity Card document is required
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-4">
                  {userVerification?.documents?.length > 0 &&
                    userVerification?.documents?.map((doc, index) => (
                      <div className="border border-gray-300 rounded-md p-4 flex items-center justify-between" key={index}>
                        <div className="flex items-center gap-3">
                          <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm truncate">Document {index + 1}</span>
                        </div>
                      </div>
                    ))}

                  {docsImages?.length > 0 &&
                    docsImages?.map((file, index) => (
                      <div className="border border-gray-300 rounded-md p-4 flex items-center justify-between" key={index}>
                        <div className="flex items-center gap-3">
                          <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          className="h-8 w-8 bg-[#FAE9E6] rounded-full flex items-center justify-center"
                          onClick={() => removeFile(index)}
                        >
                          <XIcon className="h-5 w-5 text-primary" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
              {/* grid ends */}

              <div>
                <label
                  htmlFor="url_photos"
                  className="block text-sm font-medium"
                >
                  Upload Proof Of Life
                </label>
                <div className="mt-2">
                  <button
                    className="w-full border border-[#E4E4F3] bg-[#FAFAFA] dark:bg-transparent p-3 text-center text-[#9CA3AF]"
                    onClick={handleProofClick}
                    disabled={
                      userVerification?.status === "approved" ||
                      userVerification?.status === "pending"
                    }
                  >
                    + Upload Image
                  </button>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={uploadProofOfLife}
                    ref={hiddenProofInput}
                  />
                  {submitted && livePhotos?.length < 1 && (
                    <div className="text-primary text-xs ">
                      Proof of life is required
                    </div>
                  )}
                </div>
                <div className="mt-4 ">
                  {photosUrl?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-8">
                      {photosUrl.map((url, index) => (
                        <figure
                          className="w-full h-[250px] relative"
                          key={index}
                        >
                          <img
                            src={url}
                            className="object-cover w-full h-[250px]"
                            alt="banner"
                          />
                          <button
                            type="button"
                            className="h-8 w-8 bg-[#FAE9E6] rounded-full absolute right-2 top-2"
                            onClick={() => removeProofPhoto(index)}
                          >
                            <XIcon className="h-5 w-5 text-primary mx-auto" />
                          </button>
                        </figure>
                      ))}
                    </div>
                  ) : (
                    userVerification?.pictures?.length > 0 && (
                      <div className="grid grid-cols-1 gap-8">
                        {userVerification?.pictures.map((banner, index) => (
                          <figure
                            className="w-full h-[250px] relative"
                            key={index}
                          >
                            <img
                              src={banner}
                              className="object-cover w-full h-[250px]"
                              alt="banner"
                            />
                          </figure>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>



            <div className="my-10 flex flex-col items-center justify-center">
              {userVerification?.status === "approved" ||
                userVerification?.status === "pending" ? (
                ""
              ) : (
                <div>
                  <Button.Primary
                    type="button"
                    disabled={isLoading}
                    title={isLoading ? <Beat color="#ffffff" /> : "Submit"}
                    className={
                      "sm:block rounded-md mb-6 sm:mb-0 w-full sm:w-[333px]"
                    }
                    onClick={(e) => handleSubmit(e)}
                  />

                  <p className="pb-4">By clicking on the submit button you accept our Terms of service</p>

                </div>
              )}
            </div>

          </form>
        ) : userVerification?.status === "rejected" ? (
          <EditKYCVerification />
        ) : (
          ""
        )}

      </div>

      <div className="mt-10">
        <Button.Secondary
          type="button"
          title={"Back"}
          className={"sm:hidden rounded-md mb-8 sm:mb-0 w-full"}
          onClick={() => navigate("/profile")}
        />
      </div>

    </div>
  );
}
